import { useEffect, useState } from 'react';
import { hrApi } from '../../api/hr.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function Leave() {
  const { can } = useAuth();
  const isManager = can('leave_request', 'approve');

  const [employee, setEmployee] = useState(null);
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState([]);
  const [pendingForApproval, setPendingForApproval] = useState([]);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    hrApi.getMyEmployee().then((d) => setEmployee(d?.employee)).catch(() => {});
    hrApi.leaveTypes().then((d) => setTypes(d?.items || []));
    hrApi.leaveRequests().then((d) => setRequests(d?.items || []));
    if (isManager) hrApi.leaveRequests({ status: 'pending' }).then((d) => setPendingForApproval(d?.items || []));
  }, [isManager]);

  useEffect(() => {
    if (employee?.id) hrApi.leaveBalance(employee.id).then((d) => setBalance(d?.balance || []));
  }, [employee?.id]);

  async function refreshAll() {
    const [me, mine, pend] = await Promise.all([
      employee?.id ? hrApi.leaveBalance(employee.id) : Promise.resolve(null),
      hrApi.leaveRequests(),
      isManager ? hrApi.leaveRequests({ status: 'pending' }) : Promise.resolve(null),
    ]);
    if (me) setBalance(me.balance || []);
    setRequests(mine?.items || []);
    if (pend) setPendingForApproval(pend.items || []);
  }

  async function decide(id, decision) {
    try {
      await hrApi.decideLeave(id, decision, decision === 'rejected' ? 'Declined' : null);
      await refreshAll();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed');
    }
  }
  async function cancel(id) {
    try {
      await hrApi.cancelLeave(id);
      await refreshAll();
    } catch (e) {
      setError(e.response?.data?.message);
    }
  }

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await hrApi.createLeaveRequest({
        leaveTypeId: fd.get('leaveTypeId'),
        startDate: fd.get('startDate'),
        endDate: fd.get('endDate'),
        reason: fd.get('reason') || undefined,
      });
      e.currentTarget.reset();
      setShowForm(false);
      refreshAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section>
      <header className="page-header">
        <h1>Leave</h1>
        <button className="btn primary" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Close' : '+ Request Leave'}
        </button>
      </header>

      {showForm && (
        <form onSubmit={submit} className="card form-card grid-form">
          <label>
            Type
            <select name="leaveTypeId" required>
              <option value="">Select…</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label>Start<input name="startDate" type="date" required /></label>
          <label>End<input name="endDate" type="date" required /></label>
          <label className="full">Reason<textarea name="reason" rows={2} /></label>
          <div className="full actions">
            <button type="submit" className="btn primary">Submit</button>
          </div>
        </form>
      )}

      {error && <div className="alert error">{error}</div>}

      <div className="card">
        <h3>My balance</h3>
        <div className="balance-grid">
          {balance.map((b) => (
            <div key={b.leaveTypeId} className="balance-card" style={{ borderLeftColor: b.color || '#94a3b8' }}>
              <div className="name">{b.name}</div>
              <div className="big">{b.remaining}</div>
              <div className="muted small">of {b.entitled} · {b.used} used</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>My requests</h3>
        <table className="data-table">
          <thead><tr><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id}>
                <td><span className="dot" style={{ background: r.leave_type_color || '#94a3b8' }} /> {r.leave_type_name}</td>
                <td>{r.start_date}</td>
                <td>{r.end_date}</td>
                <td>{r.days}</td>
                <td><span className={`chip status-${r.status}`}>{r.status}</span></td>
                <td>
                  {(r.status === 'pending' || r.status === 'approved') && (
                    <button className="btn ghost small" onClick={() => cancel(r.id)}>Cancel</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isManager && (
        <div className="card">
          <h3>Approval queue ({pendingForApproval.length})</h3>
          <table className="data-table">
            <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th></th></tr></thead>
            <tbody>
              {pendingForApproval.map((r) => (
                <tr key={r.id}>
                  <td>{r.employee_name} <span className="muted small">{r.employee_code}</span></td>
                  <td>{r.leave_type_name}</td>
                  <td>{r.start_date}</td>
                  <td>{r.end_date}</td>
                  <td>{r.days}</td>
                  <td className="muted">{r.reason}</td>
                  <td>
                    <button className="btn primary small" onClick={() => decide(r.id, 'approved')}>Approve</button>
                    <button className="btn ghost small" onClick={() => decide(r.id, 'rejected')}>Reject</button>
                  </td>
                </tr>
              ))}
              {pendingForApproval.length === 0 && <tr><td colSpan={7} className="muted">No pending requests.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
